import { useEffect, useState } from 'react';

import close from 'assets/filters/close.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import ResetButton from 'common-ui/resetButton';
import { AsyncSelect } from 'common-ui/select';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';

import { useCustomerManagementSearchData } from '../../useSearchData';
import {
  ClearAllRow,
  ClearAllText,
  CloseIconWrapper,
  CustomDatePickerWrapper,
  FilterContainer,
  FilterRow,
  Icon,
  InputWrapper,
  Line,
} from './Filter.styles';

const OFFER_OPTIONS = [
  { value: 'Premium Package', label: 'Premium Package' },
  { value: 'Basic Package', label: 'Basic Package' },
  { value: 'Enterprise Package', label: 'Enterprise Package' },
];

const getSearchAndFilterCount = (fieldData) => {
  const searchKeys = ['searchId', 'searchName'];
  const filterKeys = ['offer', 'createdDateFrom', 'createdDateTo'];

  const searchCount = searchKeys.reduce((count, key) => {
    const value = fieldData[key];
    return count + (typeof value === 'string' && value.trim() !== '' ? 1 : 0);
  }, 0);

  const filterCount = filterKeys.reduce((count, key) => {
    const value = fieldData[key];
    if (key === 'createdDateFrom' || key === 'createdDateTo') {
      return count + (value ? 1 : 0);
    }
    return count + (typeof value === 'string' && value.trim() !== '' ? 1 : 0);
  }, 0);

  return { searchCount, filterCount };
};

const Filter = ({ searchData, setSearchData }) => {
  const { resetSearchData } = useCustomerManagementSearchData();
  const [show, setShow] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [first, setFirst] = useState(false);
  const [fieldData, setFieldData] = useState({
    searchId: '',
    searchName: '',
    offer: '',
    createdDateFrom: null,
    createdDateTo: null,
  });

  const { searchCount, filterCount } = getSearchAndFilterCount(fieldData);

  const debouncedSearchId = useDebounce(fieldData.searchId, 500);
  const debouncedSearchName = useDebounce(fieldData.searchName, 500);

  useEffect(() => {
    if (first) setSearchData({ ...fieldData, searchId: debouncedSearchId });
  }, [debouncedSearchId]);

  useEffect(() => {
    if (first) setSearchData({ ...fieldData, searchName: debouncedSearchName });
  }, [debouncedSearchName]);

  const handleClickShowHideFilters = () => {
    setShow(!show);
    if (!show) {
      setShowSearch(false);
    }
  };

  const handleClickShowHideSearch = () => {
    setShowSearch(!showSearch);
    if (!showSearch) {
      setShow(false);
    }
  };

  const handleSearchChange = (field, value) => {
    setFirst(true);
    setFieldData({ ...fieldData, [field]: value });
    setSearchData({ ...fieldData, [field]: value });
  };

  const handleInputChange = (field, value) => {
    setFirst(true);
    setFieldData({ ...fieldData, [field]: value });
  };

  const handleClearSearch = () => {
    const clearedSearchData = {
      ...fieldData,
      searchId: '',
      searchName: '',
    };
    setFieldData(clearedSearchData);
    setSearchData(clearedSearchData);
  };

  const handleClearFilters = () => {
    const clearedFilterData = {
      ...fieldData,
      offer: '',
      createdDateFrom: null,
      createdDateTo: null,
    };
    setFieldData(clearedFilterData);
    setSearchData(clearedFilterData);
  };

  const handleResetAll = () => {
    resetSearchData();
  };

  useEffect(() => {
    setFieldData({
      searchId: searchData.searchId || '',
      searchName: searchData.searchName || '',
      offer: searchData.offer || '',
      createdDateFrom: searchData.createdDateFrom || null,
      createdDateTo: searchData.createdDateTo || null,
    });
  }, [searchData]);

  return (
    <FilterContainer>
      <FilterRow style={{ alignItems: 'center' }}>
        <Button
          outlined
          onClick={handleClickShowHideSearch}
          className="action-button"
          active={searchCount > 0}
          variant="search"
        >
          Search{searchCount > 0 ? ` (${searchCount})` : ''}
        </Button>
        <Button
          outlined
          onClick={handleClickShowHideFilters}
          className="action-button"
          active={filterCount > 0}
          variant="filter"
        >
          Filter{filterCount > 0 ? ` (${filterCount})` : ''}
        </Button>
        {(searchCount > 0 || filterCount > 0) && <ResetButton onClick={handleResetAll} />}
      </FilterRow>
      {showSearch && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={handleClickShowHideSearch}>
              <Icon src={close} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <InputWrapper>
              <Input
                placeholder="Enter Customer ID..."
                value={fieldData.searchId}
                onChange={(e) => handleInputChange('searchId', e.target.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <Input
                placeholder="Enter customer name..."
                value={fieldData.searchName}
                onChange={(e) => handleInputChange('searchName', e.target.value)}
              />
            </InputWrapper>
          </FilterRow>
          <ClearAllRow>
            <ClearAllText onClick={handleClearSearch}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}
      {show && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={handleClickShowHideFilters}>
              <Icon src={close} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <AsyncSelect
              minHeight="38px"
              defaultOptions={OFFER_OPTIONS}
              value={
                fieldData.offer
                  ? OFFER_OPTIONS.find((opt) => opt.value === fieldData.offer)
                  : null
              }
              onChange={(selectedOption) => {
                handleSearchChange('offer', selectedOption?.value);
              }}
              placeholder="Offer Type"
              isClearable
              isSearchable={false}
            />
            <CustomDatePickerWrapper>
              <CustomDatePicker
                value={fieldData.createdDateFrom}
                onChange={(date) => handleSearchChange('createdDateFrom', date)}
                placeholder="Created from"
                disableFuture
                maxDate={fieldData.createdDateTo ? dayjs(fieldData.createdDateTo) : null}
              />
            </CustomDatePickerWrapper>
            <CustomDatePickerWrapper>
              <CustomDatePicker
                value={fieldData.createdDateTo}
                onChange={(date) => handleSearchChange('createdDateTo', date)}
                placeholder="Created to"
                disableFuture
                minDate={fieldData.createdDateFrom ? dayjs(fieldData.createdDateFrom) : null}
              />
            </CustomDatePickerWrapper>
          </FilterRow>
          <ClearAllRow>
            <ClearAllText onClick={handleClearFilters}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}
    </FilterContainer>
  );
};

export default Filter;
